import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { 
  getERPClients, createERPClient, updateERPClient, deleteERPClient 
} from '@/lib/api';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Search, Loader2, 
  Users, Phone, Mail, Building2, IndianRupee 
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  address: '',
  gst_number: '',
  monthly_billing: 0,
  notes: '',
  active: true,
};

export default function ERPClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await getERPClients(false);
      setClients(res.data);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterActive === 'all' || 
      (filterActive === 'active' && client.active) ||
      (filterActive === 'inactive' && !client.active);
    
    return matchesSearch && matchesFilter;
  });

  const handleEdit = (client) => {
    setEditing(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      address: client.address || '',
      gst_number: client.gst_number || '',
      monthly_billing: client.monthly_billing || 0,
      notes: client.notes || '',
      active: client.active,
    });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setFormData(initialFormState);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateERPClient(editing.id, formData);
        toast.success('Client updated');
      } else {
        await createERPClient(formData);
        toast.success('Client added');
      }
      loadClients();
      setDialogOpen(false);
    } catch (err) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await deleteERPClient(id);
      toast.success('Client deleted');
      loadClients();
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const totalMonthlyBilling = clients
    .filter(c => c.active)
    .reduce((sum, c) => sum + (c.monthly_billing || 0), 0);

  return (
    <AdminLayout title="ERP - Clients">
      <div className="space-y-6" data-testid="erp-clients">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Client Management</h1>
            <p className="text-[#888] text-sm">Manage your client database and billing</p>
          </div>
          <button
            onClick={handleAdd}
            data-testid="add-client-btn"
            className="flex items-center gap-2 bg-[#ff5a1f] text-white px-5 py-3 text-sm font-semibold hover:bg-[#e04e1a] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-4">
            <p className="text-[#888] text-sm">Total Clients</p>
            <p className="font-syne text-2xl font-bold text-white">{clients.length}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-4">
            <p className="text-[#888] text-sm">Active Clients</p>
            <p className="font-syne text-2xl font-bold text-green-500">
              {clients.filter(c => c.active).length}
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-4">
            <p className="text-[#888] text-sm">Inactive Clients</p>
            <p className="font-syne text-2xl font-bold text-[#555]">
              {clients.filter(c => !c.active).length}
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-4">
            <p className="text-[#888] text-sm">Expected Monthly</p>
            <p className="font-syne text-2xl font-bold text-[#ff5a1f]">
              {formatCurrency(totalMonthlyBilling)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555]" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-clients"
              className="w-full bg-[#0A0A0A] border border-[#1F1F1F] text-white pl-12 pr-4 py-3 text-sm outline-none focus:border-[#ff5a1f]"
            />
          </div>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="bg-[#0A0A0A] border border-[#1F1F1F] text-white px-4 py-3 text-sm outline-none"
          >
            <option value="all">All Clients</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <p className="text-[#555]">No clients found</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                data-testid={`client-card-${client.id}`}
                className={`bg-[#0A0A0A] border p-6 transition-colors ${
                  client.active ? 'border-[#1F1F1F]' : 'border-[#1F1F1F] opacity-60'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-syne text-lg font-bold text-white">{client.name}</h3>
                      <span className={`text-xs px-2 py-1 ${
                        client.active 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-red-500/10 text-red-500'
                      }`}>
                        {client.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      {client.company && (
                        <div className="flex items-center gap-2 text-[#888]">
                          <Building2 className="w-4 h-4" />
                          {client.company}
                        </div>
                      )}
                      {client.email && (
                        <div className="flex items-center gap-2 text-[#888]">
                          <Mail className="w-4 h-4" />
                          {client.email}
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center gap-2 text-[#888]">
                          <Phone className="w-4 h-4" />
                          {client.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[#ff5a1f]">
                        <IndianRupee className="w-4 h-4" />
                        {formatCurrency(client.monthly_billing)}/mo
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(client)}
                      data-testid={`edit-client-${client.id}`}
                      className="p-2 text-[#888] hover:text-[#ff5a1f] transition-colors"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      data-testid={`delete-client-${client.id}`}
                      className="p-2 text-[#888] hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-syne text-xl">
                {editing ? 'Edit Client' : 'Add New Client'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="client-name-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="Client name"
                  />
                </div>
                <div>
                  <label className="block text-[#888] text-sm mb-2">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="Company name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="client-email-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="client@example.com"
                  />
                </div>
                <div>
                  <label className="block text-[#888] text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="client-phone-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#888] text-sm mb-2">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                  placeholder="Full address"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#888] text-sm mb-2">GST Number</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="GSTIN"
                  />
                </div>
                <div>
                  <label className="block text-[#888] text-sm mb-2">Monthly Billing (INR)</label>
                  <input
                    type="number"
                    value={formData.monthly_billing}
                    onChange={(e) => setFormData({ ...formData, monthly_billing: parseFloat(e.target.value) || 0 })}
                    data-testid="client-billing-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#888] text-sm mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 accent-[#ff5a1f]"
                  />
                  <span className="text-white text-sm">Active Client</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-client-btn"
                  className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editing ? 'Update Client' : 'Add Client')}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
